import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useRouter } from 'next/dist/client/router';
import Link from 'next/link';
import React, { FC, useState, useContext } from 'react';
import useIsBreakpoint from '../../../hooks/use-is-breakpoint/use-is-breakpoint';
import useOrderStatusColor from '../../../hooks/use-order-status-color/use-order-status-color';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import BreakpointEnum from '../../../models/breakpoint-enum';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import { OrderType } from '../../../models/order-type';
import { OrdersListingType } from '../../../models/orders-listing-type';
import PlacementEnum from '../../../models/placement-enum';
import IcomoonIconButton from '../../atoms/icomoon-icon-button/icomoon-icon-button';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import TableData from '../../atoms/table-data/table-data';
import TableRow from '../../atoms/table-row/table-row';

dayjs.extend(advancedFormat);

interface Props extends OrdersListingType, OrderType {}

const OrdersListingTableRow: FC<Props> = ({
  confirmationNumber,
  confirmationNumberColumnLabel,
  orderDetailsPage,
  orderPlacedColumnLabel,
  orderPlacedDate,
  reference,
  status,
  statusColumnLabel,
  total,
  totalColumnLabel,
  tracking,
  yourReferenceColumnLabel,
}: Props) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const breakpoint = useIsBreakpoint();
  const router = useRouter();
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const statusColor = useOrderStatusColor(status);

  return (
    <>
      <TableRow
        className="bg-grey-50 hover:bg-light-blue cursor-pointer grid grid-cols-2 md:grid-cols-3"
        onBlur={() => setIsFocused(false)}
        onClick={() => {
          router.push(
            `${orderDetailsPage.url.substring(
              0,
              orderDetailsPage.url.length - 1
            )}?orderConfirmationNumber=${confirmationNumber}`
          );

          pushToDataLayer(GtmEventNameEnum.OrderView, {
            placement: PlacementEnum.OrderListing,
          });
        }}
        onFocus={() => setIsFocused(true)}
        onMouseLeave={() => setIsFocused(false)}
        onMouseOver={() => setIsFocused(true)}
      >
        <TableData className="rounded-l-lg" label={orderPlacedColumnLabel}>
          <span>
            {dayjs(orderPlacedDate).format(
              breakpoint === BreakpointEnum.sm ||
                breakpoint === BreakpointEnum.lg
                ? 'DD.MM.YYYY'
                : 'Do MMMM YYYY'
            )}
          </span>
        </TableData>

        <TableData label={confirmationNumberColumnLabel}>
          <span className="truncate">#{confirmationNumber}</span>
        </TableData>

        <TableData label={yourReferenceColumnLabel}>
          <span>{reference}</span>
        </TableData>

        <TableData label={statusColumnLabel}>
          <div className="flex items-center">
            <div className={`bg-${statusColor} h-2 mr-2 rounded-full w-2`} />
            <span>{status}</span>
          </div>

          {tracking.map((trackingLink: string) => (
            <Link href={trackingLink} passHref>
              <a
                href="replace"
                onClick={(event) => {
                  event.stopPropagation();

                  pushToDataLayer(GtmEventNameEnum.OrderTrack, {
                    placement: PlacementEnum.OrderListing,
                  });
                }}
                target="_blank"
              >
                <IcomoonIcon
                  className="mr-1"
                  color="french-blue"
                  name="open"
                  size="sm"
                />

                <span className="text-french-blue underline whitespace-nowrap">
                  {trackingLink.substring(
                    trackingLink.indexOf('trackingnumber=') +
                      'trackingnumber='.length,
                    trackingLink.length
                  )}
                </span>
              </a>
            </Link>
          ))}
        </TableData>

        <TableData label={totalColumnLabel}>
          <span>{total}</span>
        </TableData>

        <td
          className="border-t lg:border-t-0 border-grey-200 col-span-full mt-1 mx-6 lg:mt-0 lg:mx-0 lg:rounded-r-lg"
          style={{ width: 'calc(100% - 3rem)' }}
        >
          <div className="flex items-center justify-center">
            <IcomoonIconButton
              className="-mb-2px"
              color={isFocused ? 'french-blue' : 'grey-600'}
              name="chevron-right"
              onClick={() => {
                router.push(
                  `${orderDetailsPage.url.substring(
                    0,
                    orderDetailsPage.url.length - 1
                  )}?orderConfirmationNumber=${confirmationNumber}`
                );

                pushToDataLayer(GtmEventNameEnum.OrderView, {
                  placement: PlacementEnum.OrderListing,
                });
              }}
            />

            <span className="lg:hidden text-sm">View order</span>
          </div>
        </td>
      </TableRow>

      <tr className="h-3" />
    </>
  );
};

export default OrdersListingTableRow;
